import { IJob } from "@/lib/database/schema/job";

export class Job implements IJob {
  id?: string;
  title: string;
  cleaned_title?: string;
  location?: string;
  description: string;
  linkedin_id: string;
  company_id: string;
  linkedin_url: string;
  work_remote_allowed?: boolean;
  applies?: number;
  company_apply_url?: string;
  linkedin_easy_apply_url?: string;
  rapid_job_response?: any;
  rapid_hiring_team_response?: any;
  rapid_salary_response?: any;
  created_at?: string;
  updated_at?: string;

  constructor(jobData: IJob) {
    this.id = jobData.id;
    this.title = jobData.title;
    this.cleaned_title = jobData.cleaned_title;
    this.location = jobData.location;
    this.description = jobData.description;
    this.linkedin_id = jobData.linkedin_id;
    this.company_id = jobData.company_id;
    this.linkedin_url = jobData.linkedin_url;
    this.work_remote_allowed = jobData.work_remote_allowed;
    this.applies = jobData.applies;
    this.company_apply_url = jobData.company_apply_url;
    this.linkedin_easy_apply_url = jobData.linkedin_easy_apply_url;
    this.rapid_job_response = jobData.rapid_job_response;
    this.rapid_hiring_team_response = jobData.rapid_hiring_team_response;
    this.rapid_salary_response = jobData.rapid_salary_response;
    this.created_at = jobData.created_at;
    this.updated_at = jobData.updated_at;
  }

  /**
   * Check if job allows remote work
   */
  isRemoteAllowed(): boolean {
    return !!this.work_remote_allowed;
  }

  /**
   * Check if job has location information
   */
  hasLocation(): boolean {
    return !!this.location;
  }

  /**
   * Check if job has application count data
   */
  hasApplicationCount(): boolean {
    return !!this.applies && this.applies > 0;
  }

  /**
   * Check if job has easy apply option
   */
  hasEasyApply(): boolean {
    return !!this.linkedin_easy_apply_url;
  }

  /**
   * Check if job has company apply URL
   */
  hasCompanyApplyUrl(): boolean {
    return !!this.company_apply_url;
  }

  /**
   * Check if job has hiring team data
   */
  hasHiringTeamData(): boolean {
    return !!this.rapid_hiring_team_response;
  }

  /**
   * Check if job has salary data
   */
  hasSalaryData(): boolean {
    return !!this.rapid_salary_response;
  }

  /**
   * Get job title (cleaned if available, otherwise original)
   */
  getDisplayTitle(): string {
    return this.cleaned_title || this.title || 'Unknown Position';
  }

  /**
   * Get application competition level based on applies count
   */
  getCompetitionLevel(): string {
    if (!this.applies) return 'Unknown';
    
    if (this.applies <= 10) return 'Low';
    if (this.applies <= 50) return 'Moderate';
    if (this.applies <= 200) return 'High';
    return 'Very High';
  }

  /**
   * Check if job posting is complete
   */
  isComplete(): boolean {
    return !!(this.title && this.description && this.linkedin_id && this.company_id);
  }

  /**
   * Get work arrangement type
   */
  getWorkArrangement(): string {
    if (this.work_remote_allowed) {
      return this.location ? 'Hybrid/Remote' : 'Remote';
    }
    return this.location ? 'On-site' : 'Location TBD';
  }

  /**
   * Check if job has complete application data
   */
  hasApplicationOptions(): boolean {
    return this.hasEasyApply() || this.hasCompanyApplyUrl();
  }

  /**
   * Convert back to IJob interface
   */
  toIJob(): IJob {
    return {
      id: this.id,
      title: this.title,
      cleaned_title: this.cleaned_title,
      location: this.location,
      description: this.description,
      linkedin_id: this.linkedin_id,
      company_id: this.company_id,
      linkedin_url: this.linkedin_url,
      work_remote_allowed: this.work_remote_allowed,
      applies: this.applies,
      company_apply_url: this.company_apply_url,
      linkedin_easy_apply_url: this.linkedin_easy_apply_url,
      rapid_job_response: this.rapid_job_response,
      rapid_hiring_team_response: this.rapid_hiring_team_response,
      rapid_salary_response: this.rapid_salary_response,
      created_at: this.created_at,
      updated_at: this.updated_at,
    };
  }
}
